import React, { useMemo } from 'react';
import type { INestedConfigFormat } from '../utils/configEditorTypes';

interface ObjectCounts {
  databases: number;
  schemas: number;
  tables: number;
  views: number;
  procedures: number;
  functions: number;
  file_formats: number;
  sequences: number;
}

interface SoleObjectCountsProps {
  nestedSoleConfig: INestedConfigFormat | undefined;
}

export default function SoleObjectCounts({ nestedSoleConfig }: SoleObjectCountsProps): JSX.Element {
  // Calculate object counts whenever nestedSoleConfig changes
  const objectCounts = useMemo<ObjectCounts>(() => {
    const counts: ObjectCounts = {
      databases: 0,
      schemas: 0,
      tables: 0,
      views: 0,
      procedures: 0,
      functions: 0,
      file_formats: 0,
      sequences: 0,
    };

    if (nestedSoleConfig?.databases !== undefined && nestedSoleConfig.databases !== null) {
      counts.databases = nestedSoleConfig.databases.length;

      // Count schemas, tables, views, procedures, functions, file_formats, and sequences
      nestedSoleConfig.databases.forEach((database) => {
        counts.schemas += database.schemas.length;

        database.schemas.forEach((schema) => {
          counts.tables += schema.tables.length;
          counts.views += schema.views.length;
          counts.procedures += schema.procedures.length;
          counts.functions += schema.functions.length;
          counts.file_formats += schema.file_formats.length;
          counts.sequences += schema.sequences.length;
        });
      });
    }

    return counts;
  }, [nestedSoleConfig]);

  // Calculate how many items to display
  const itemsToShow = [
    objectCounts.databases > 0,
    objectCounts.schemas > 0,
    objectCounts.tables > 0,
    objectCounts.views > 0,
    objectCounts.procedures > 0,
    objectCounts.functions > 0,
    objectCounts.file_formats > 0,
    objectCounts.sequences > 0,
  ].filter(Boolean).length;

  if (itemsToShow === 0) {
    return <></>;
  }

  return (
    <div className="border border-gray-300 rounded-xl p-2">
      <div
        className="grid divide-x divide-gray-300"
        style={{
          gridTemplateColumns: `repeat(${itemsToShow}, 1fr)`,
        }}
      >
        {objectCounts.databases > 0 && (
          <div className="text-center p-2 overflow-hidden">
            <div className="text-sm text-gray-600 whitespace-nowrap overflow-hidden text-ellipsis">Databases</div>
            <div className="text-2xl font-bold">{objectCounts.databases}</div>
          </div>
        )}
        {objectCounts.schemas > 0 && (
          <div className="text-center p-2 overflow-hidden">
            <div className="text-sm text-gray-600 whitespace-nowrap overflow-hidden text-ellipsis">Schemas</div>
            <div className="text-2xl font-bold">{objectCounts.schemas}</div>
          </div>
        )}
        {objectCounts.tables > 0 && (
          <div className="text-center p-2 overflow-hidden">
            <div className="text-sm text-gray-600 whitespace-nowrap overflow-hidden text-ellipsis">Tables</div>
            <div className="text-2xl font-bold">{objectCounts.tables}</div>
          </div>
        )}
        {objectCounts.views > 0 && (
          <div className="text-center p-2 overflow-hidden">
            <div className="text-sm text-gray-600 whitespace-nowrap overflow-hidden text-ellipsis">Views</div>
            <div className="text-2xl font-bold">{objectCounts.views}</div>
          </div>
        )}
        {objectCounts.procedures > 0 && (
          <div className="text-center p-2 overflow-hidden">
            <div className="text-sm text-gray-600 whitespace-nowrap overflow-hidden text-ellipsis">Procedures</div>
            <div className="text-2xl font-bold">{objectCounts.procedures}</div>
          </div>
        )}
        {objectCounts.functions > 0 && (
          <div className="text-center p-2 overflow-hidden">
            <div className="text-sm text-gray-600 whitespace-nowrap overflow-hidden text-ellipsis">Functions</div>
            <div className="text-2xl font-bold">{objectCounts.functions}</div>
          </div>
        )}
        {objectCounts.file_formats > 0 && (
          <div className="text-center p-2 overflow-hidden">
            <div className="text-sm text-gray-600 whitespace-nowrap overflow-hidden text-ellipsis">File Formats</div>
            <div className="text-2xl font-bold">{objectCounts.file_formats}</div>
          </div>
        )}
        {objectCounts.sequences > 0 && (
          <div className="text-center p-2 overflow-hidden">
            <div className="text-sm text-gray-600 whitespace-nowrap overflow-hidden text-ellipsis">Sequences</div>
            <div className="text-2xl font-bold">{objectCounts.sequences}</div>
          </div>
        )}
      </div>
    </div>
  );
}
